import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { AiChange, Beat, JournalEntry, JournalQuestData } from '@roll1/shared';
import { pluralize, useI18n } from '../../i18n';
import type { TranslationKeys } from '../../i18n';
import { useSessionContext } from '../../contexts/SessionContext';
import { useJournalContext } from '../../contexts/JournalContext';
import { changeFieldName, changeValue } from './changeFields';
import { Button } from '../ui/button';
import { summarizeChanges } from './changeSummary';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { MoreHorizontal, Check, History } from 'lucide-react';

/**
 * The card of one answer of the AI (D-task AU-5 / AU-18; mockup studio-ai-chat-preview-v2.html turnHtml(), changeHtml()):
 * «Changes (N)» with «Undo all», a line per change — a sign, the thing as a link, «Undo» / «Undo (+N related)» —
 * a was / now for a text edit, ↺ and a strike-through for what is undone. One card per answer, however many steps.
 * Used by the Studio column and the table's chat; the link opens the thing in Studio (the host decides what a click does).
 * Several changes of ONE thing (forty fields of a sheet filled in one answer) are one line «Sheet “…” — 40 changes»
 * with its own «Undo» and a list that opens: each field by its name, was → now (owner, 23.10).
 * Studio's colours (neutral-*, amber-400, blue-300 for «Tia»).
 */

type Translate = (key: keyof TranslationKeys) => string;

export interface ChangeLookup {
  beat: (id: string) => Beat | undefined;
  /** The beat whose prep text is this entry. */
  beatOfPrep: (entryId: string) => Beat | undefined;
  entry: (id: string) => JournalEntry | undefined;
}

/** A line: the text around the thing's name, and the name that is the link. */
export interface ChangeLine { readonly before: string; readonly name: string; readonly after: string }

const fillText = (template: string, values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [k, v]) => text.split(`{${k}}`).join(v), template);

/** The sentence of a change (from kind / op / name — the server does not word it) around the name that links. */
export function changeLine(ch: AiChange, lookup: ChangeLookup, t: Translate): ChangeLine {
  const name = ch.entity.name;
  const sentence = (key: keyof TranslationKeys, extra: Record<string, string> = {}): ChangeLine => {
    const [before = '', after = ''] = fillText(t(key), { ...extra, name: '\u0000' }).split('\u0000');
    return { before, name, after };
  };
  const beatName = (id: string | undefined): string => {
    const b = id === undefined ? undefined : lookup.beat(id);
    return b?.sessionLabel ?? '';
  };
  switch (ch.entity.kind) {
    case 'beat':
      if (ch.op === 'delete') return sentence('aiChange.deleted');
      if (ch.op === 'create') return sentence(lookup.beat(ch.entity.id)?.reserveOf ? 'aiChange.reserveCreated' : 'aiChange.beatCreated');
      if (ch.field === 'currentBeatId') return sentence('aiChange.currentBeat');
      if (ch.field === 'beatOrder') return sentence('aiChange.beatMoved');
      if (ch.field === 'status') return sentence('aiChange.beatStatus', { status: t(`studio.beat.status.${String(ch.after ?? '')}` as keyof TranslationKeys) });
      if (ch.field === 'sessionLabel') return sentence('aiChange.beatRenamed', { before: String(ch.before ?? '') });
      return sentence('aiChange.beatChanged');
    case 'outcome':
      return ch.op === 'delete' ? sentence('aiChange.deleted') : sentence('aiChange.outcomeCreated', { beat: beatName(ch.entity.parentId) });
    case 'journal': {
      if (ch.op === 'delete') return sentence('aiChange.deleted');
      const entry = lookup.entry(ch.entity.id);
      if (ch.op === 'create') return sentence(entry?.type === 'quest' ? 'aiChange.questCreated' : entry?.type === 'lore' ? 'aiChange.noteCreated' : 'aiChange.entryCreated');
      // The text of a beat's prep: «Prep of “…” extended».
      const prepBeat = lookup.beatOfPrep(ch.entity.id);
      if (prepBeat !== undefined) return { ...sentence('aiChange.prepExtended'), name: prepBeat.sessionLabel ?? name };
      if (ch.field === 'links') return sentence('aiChange.linkAdded');
      if (ch.field === 'tags') return sentence('aiChange.tagsChanged');
      return sentence('aiChange.entryChanged');
    }
    case 'objective': {
      const quest = lookup.entry(ch.entity.parentId ?? '');
      return sentence(ch.after === true ? 'aiChange.objectiveDone' : 'aiChange.objectiveUndone', { quest: quest?.name ?? '' });
    }
    case 'timeline':
      if (ch.op === 'delete') return sentence('aiChange.deleted');
      return ch.field === 'cursor' ? sentence('aiChange.timeMoved') : sentence('aiChange.timelineDraft');
    case 'page':
      if (ch.op === 'delete') return sentence('aiChange.deleted');
      if (ch.field === 'name') return sentence('aiChange.pageRenamed', { before: String(ch.before ?? '') });
      if (ch.field === 'fog') return sentence('aiChange.fogChanged');
      return sentence('aiChange.pageCreated');
    case 'pin': return ch.op === 'delete' ? sentence('aiChange.deleted') : sentence('aiChange.pinCreated');
    case 'token':
      if (ch.op === 'delete') return sentence('aiChange.deleted');
      if (ch.op === 'update') return ch.field === 'position' ? sentence('aiChange.tokenMoved') : sentence('aiChange.tokenChanged', { field: String(ch.field ?? '') });
      return sentence('aiChange.tokenCreated');
    case 'sheet':
      if (ch.op === 'delete') return sentence('aiChange.deleted');
      return ch.op === 'update' ? sentence('aiChange.sheetEdited') : sentence('aiChange.sheetCreated');
  }
}

/** How many not yet undone changes go with this one (what depends on it, by the chain). */
export function relatedCount(changes: readonly AiChange[], id: string): number {
  const gone = new Set<string>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const c of changes) {
      if (gone.has(c.id) || c.undone === true || c.kept === true || c.dependsOn === undefined || !gone.has(c.dependsOn)) continue;
      gone.add(c.id);
      grew = true;
    }
  }
  return gone.size - 1;
}

/** A change that can still be taken back. */
export const isLive = (c: AiChange): boolean => c.undone !== true && c.kept !== true && c.irreversible !== true;

/** Things the card can open in Studio (a chronicle draft, a pin, a token have no place of their own there). */
const OPENABLE = new Set<AiChange['entity']['kind']>(['beat', 'outcome', 'journal', 'objective', 'page', 'sheet']);

const ICON: Record<AiChange['op'], string> = { create: '+', update: '✎', delete: '−' };

/** Changes of one answer by the thing they touch, in the order of the first change of each. */
export function groupChanges(changes: readonly AiChange[]): AiChange[][] {
  const groups = new Map<string, AiChange[]>();
  const out: AiChange[][] = [];
  for (const ch of changes) {
    // A thing without an id (nothing to tell apart) and the current beat (a move of the game, not an edit) stay alone.
    const key = ch.entity.id === '' || ch.field === 'currentBeatId' ? `one:${ch.id}` : `${ch.entity.kind}:${ch.entity.id}`;
    const g = groups.get(key);
    if (g !== undefined) g.push(ch);
    else { const fresh = [ch]; groups.set(key, fresh); out.push(fresh); }
  }
  return out;
}

const GROUP_KEY: Partial<Record<AiChange['entity']['kind'], keyof TranslationKeys>> = {
  sheet: 'aiGroup.sheet', beat: 'aiGroup.beat', journal: 'aiGroup.journal', token: 'aiGroup.token', page: 'aiGroup.page',
};

export interface AiTurnCardProps {
  readonly turnId: string;
  readonly changes: readonly AiChange[];
  readonly lookup: ChangeLookup;
  readonly onUndo: (turnId: string, changeIds?: string[]) => void;
  /** A click on the name of a thing: open it. Absent — the name is plain text. */
  readonly onOpen?: ((change: AiChange) => void) | undefined;
  /** Which changes have a place to open on this surface (default: everything Studio opens). */
  readonly canOpen?: ((change: AiChange) => boolean) | undefined;
  readonly onHide?: () => void;
  readonly onRestore?: () => void;
  readonly busy?: boolean;
}

export function AiTurnCard(props: AiTurnCardProps): JSX.Element {
  const { t, locale } = useI18n();
  const [details, setDetails] = useState(false);
  const [menu, setMenu] = useState(false);
  const [undo, setUndo] = useState<{ ids?: string[] } | null>(null);
  const groups = groupChanges(props.changes);
  const names = groups.map(group => group[group.length - 1]!.entity.name).filter(Boolean);
  const summaries = groups.flatMap(summarizeChanges);
  const fields = [...new Set(summaries.filter(c => c.op === 'update').map(c => changeFieldName(c, t, locale)))];
  const undone = props.changes.filter(c => c.undone).length;
  const kept = props.changes.some(c => c.kept);
  const status = props.changes.length === 0 ? t('ai.results.noChanges') : kept ? t('ai.results.kept') : undone === props.changes.length ? t('aiCard.undone') : undone > 0 ? t('ai.results.partial') : t('ai.results.saved');
  const title = names.join(' · ') || status;
  const openable = props.changes.find(c => c.op !== 'delete' && !c.undone && OPENABLE.has(c.entity.kind) && (props.canOpen?.(c) ?? true));
  const requestUndo = (_turnId: string, ids?: string[]) => { setDetails(false); setMenu(false); setUndo({ ids }); };
  return <>
    <div className="min-w-0 rounded-lg border border-line bg-panel-2/40 px-3 py-2.5" data-testid="ai-turn-card" data-turn-id={props.turnId}>
      <div className="flex items-start gap-2">
        {undone > 0 || kept ? <History size={15} className="mt-0.5 shrink-0 text-dim" /> : <Check size={15} className="mt-0.5 shrink-0 text-ok" />}
        <div className="min-w-0 flex-1"><p className="text-meta text-muted-foreground" role="status">{status}</p><p className="break-words text-ui font-medium text-fg">{title}</p></div>
        <Popover open={menu} onOpenChange={setMenu}><PopoverTrigger asChild><Button size="icon" variant="ghost" className="size-7 shrink-0" aria-label={t('ai.results.menu')}><MoreHorizontal size={16} /></Button></PopoverTrigger><PopoverContent align="end" className="w-56 p-1">
          {props.onHide && <Button variant="ghost" className="w-full justify-start text-ui" onClick={() => { setMenu(false); props.onHide?.(); }}>{t('ai.results.hide')}</Button>}
          {props.onRestore && <Button variant="ghost" className="w-full justify-start text-ui" onClick={props.onRestore}>{t('ai.results.restore')}</Button>}
          {props.changes.some(isLive) && <Button variant="ghost" disabled={props.busy} className="w-full justify-start text-ui text-danger" onClick={() => requestUndo(props.turnId)}>{t('ai.results.undo')}</Button>}
        </PopoverContent></Popover>
      </div>
      {fields.length > 0 && <p className="mt-1 line-clamp-2 break-words text-meta text-dim">{fields.slice(0, 4).join(' · ')}{fields.length > 4 ? ` · +${fields.length - 4}` : ''}</p>}
      <div className="mt-1 flex flex-wrap gap-2">
        {props.onOpen && openable && <Button variant="text" size="sm" className="px-0 text-ai" onClick={() => props.onOpen?.(openable)}>{t('ai.results.open')}</Button>}
        {props.changes.length > 0 && <Button variant="text" size="sm" onClick={() => setDetails(true)}>{t('ai.results.details')}</Button>}
        {props.onRestore && <Button variant="text" size="sm" onClick={props.onRestore}>{t('ai.results.restore')}</Button>}
      </div>
    </div>
    <Dialog open={details} onOpenChange={setDetails}><DialogContent size="xl" className="flex max-h-[85dvh] flex-col p-5 sm:p-6"><DialogHeader className="pr-6"><DialogTitle>{title}</DialogTitle><DialogDescription>{status}</DialogDescription></DialogHeader><div className="min-h-0 overflow-y-auto"><AiTurnDetails {...props} onUndo={requestUndo} onOpen={props.onOpen ? c => { setDetails(false); props.onOpen?.(c); } : undefined} /></div></DialogContent></Dialog>
    <Dialog open={undo !== null} onOpenChange={open => { if (!open) setUndo(null); }}><DialogContent size="md"><DialogHeader><DialogTitle>{t('ai.results.undoTitle')}</DialogTitle><DialogDescription>{t('ai.results.undoBody')}</DialogDescription></DialogHeader><p className="break-words text-ui">{title}</p><div className="flex flex-wrap justify-end gap-2"><Button variant="outline" onClick={() => setUndo(null)}>{t('common.cancel')}</Button><Button variant="destructive" disabled={props.busy} onClick={() => { if (undo) props.onUndo(props.turnId, undo.ids); setUndo(null); }}>{t('aiCard.undo')}</Button></div></DialogContent></Dialog>
  </>;
}

export function AiTurnDetails({ turnId, changes, lookup, onUndo, onOpen, canOpen, busy }: AiTurnCardProps): JSX.Element {
  const { t, locale } = useI18n();
  const live = changes.filter(isLive);
  const open = changes.filter(c => c.undone !== true && c.kept !== true);
  const allDone = open.length === 0;
  const muted = allDone ? ' opacity-55' : '';
  const groups = groupChanges(changes);
  const visibleCount = groups.flatMap(summarizeChanges).filter(c => c.undone !== true && c.kept !== true).length;
  return (
    <div
      className={`flex flex-col gap-1.5 rounded-md border border-line bg-panel-2 px-2.5 py-2${muted}`}
      data-testid="ai-turn-details"
      data-turn-id={turnId}
    >
      <div className="flex items-center gap-2 text-meta text-muted-foreground">
        <span>{allDone ? t('aiCard.undone') : fillText(t('aiCard.changes'), { count: String(visibleCount) })}</span>
        <span className="flex-1" />
        {live.length > 0 ? (
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={(): void => onUndo(turnId)}>
            {t('aiCard.undoAll')}
          </Button>
        ) : null}
      </div>
      <ul className="flex flex-col gap-1.5">
        {groups.map((group) => {
          if (group.length > 1 || group[0]?.field === 'roleplay_json') {
            return (
              <ChangeGroup
                key={group[0]!.id}
                turnId={turnId}
                group={group}
                all={changes}
                lookup={lookup}
                onUndo={onUndo}
                onOpen={onOpen}
                canOpen={canOpen}
              />
            );
          }
          const ch = group[0]!;
          const line = changeLine(ch, lookup, t);
          const undone = ch.undone === true;
          const related = relatedCount(changes, ch.id);
          const textEdit = ch.op === 'update' && typeof ch.before === 'string' && typeof ch.after === 'string' && ch.entity.kind === 'journal';
          return (
            <li key={ch.id} className="flex flex-col gap-1" data-testid="ai-change" data-op={ch.op} data-undone={undone ? 'true' : undefined}>
              <div className="flex items-start gap-1.5 text-body-s text-fg">
                <span className="w-3.5 flex-none text-ai" aria-hidden="true">{undone ? '↺' : ICON[ch.op]}</span>
                <span className={`min-w-0 flex-1${undone ? ' line-through text-dim' : ''}`}>
                  {line.before}
                  {onOpen !== undefined && !undone && ch.op !== 'delete' && OPENABLE.has(ch.entity.kind) && (canOpen === undefined || canOpen(ch)) ? (
                    <span
                      role="button"
                      tabIndex={0}
                      className="cursor-pointer text-left text-hl hover:underline"
                      onClick={(): void => onOpen(ch)}
                      onKeyDown={(e): void => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(ch); } }}
                    >
                      {line.name}
                    </span>
                  ) : (
                    <span className={undone ? 'text-dim' : 'text-hl'}>{line.name}</span>
                  )}
                  {line.after}
                  {textEdit && ch.field !== undefined ? <span className="text-dim"> · {(['description', 'content', 'gmNotes', 'rewards'] as const).includes(ch.field as 'description') ? t(`aiCard.field.${ch.field}` as keyof TranslationKeys) : ch.field}</span> : null}
                  {!textEdit && ch.op === 'update' && ch.entity.kind === 'sheet' && ch.field !== undefined ? <span className="text-dim" data-testid="ai-change-detail"> · {changeFieldName(ch, t, locale)}</span> : null}
                  {ch.kept === true ? <span className="text-dim"> {t('aiCard.kept')}</span> : null}
                  {ch.irreversible === true && !undone ? <span className="text-dim"> · {t('aiCard.irreversible')}</span> : null}
                </span>
                {isLive(ch) ? (
                  <button
                    type="button"
                    className="flex-none rounded border border-transparent px-1.5 text-meta text-dim hover:border-line hover:text-fg"
                    onClick={(): void => onUndo(turnId, [ch.id])}
                  >
                    {related > 0 ? fillText(t('aiCard.undoRelated'), { count: String(related) }) : t('aiCard.undo')}
                  </button>
                ) : null}
              </div>
              {textEdit || (ch.op === 'update' && ch.entity.kind === 'sheet' && ch.field !== undefined) ? (
                <FieldDifference change={ch} />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface ChangeGroupProps {
  readonly turnId: string;
  readonly group: readonly AiChange[];
  readonly all: readonly AiChange[];
  readonly lookup: ChangeLookup;
  readonly onUndo: (turnId: string, changeIds?: string[]) => void;
  readonly onOpen?: ((change: AiChange) => void) | undefined;
  readonly canOpen?: ((change: AiChange) => boolean) | undefined;
}

/** One thing changed several times in the answer: one line, one «Undo» for all of it, its fields under «show». */
function ChangeGroup({ turnId, group, all, lookup, onUndo, onOpen, canOpen }: ChangeGroupProps): JSX.Element {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const first = group[0]!;
  const last = group[group.length - 1]!;
  const name = last.entity.name || first.entity.name;
  const [before = '', after = ''] = fillText(t(GROUP_KEY[first.entity.kind] ?? 'aiGroup.other'), { name: '\u0000' }).split('\u0000');
  const summary = summarizeChanges(group);
  const n = summary.length;
  const countText = fillText(locale === 'ru' ? pluralize(n, t('aiGroup.count.one'), t('aiGroup.count.few'), t('aiGroup.count.many')) : (n === 1 ? t('aiGroup.count.one') : t('aiGroup.count.many')), { n: String(n) });
  const allUndone = group.every(c => c.undone === true);
  const live = group.filter(isLive);
  const inGroup = new Set(group.map(c => c.id));
  // What goes with the group from outside it (a note in a beat of the group), said before the click.
  const related = new Set<string>();
  for (const c of live) {
    const gone = new Set<string>([c.id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const x of all) {
        if (gone.has(x.id) || x.undone === true || x.kept === true || x.dependsOn === undefined || !gone.has(x.dependsOn)) continue;
        gone.add(x.id);
        grew = true;
      }
    }
    for (const id of gone) if (!inGroup.has(id)) related.add(id);
  }
  const openable = onOpen !== undefined && !allUndone && OPENABLE.has(first.entity.kind) && group.some(c => c.op !== 'delete') && (canOpen === undefined || canOpen(last));
  const op: AiChange['op'] = group.some(c => c.op === 'create') ? 'create' : group.every(c => c.op === 'delete') ? 'delete' : 'update';
  const beatName = (id: string): string | undefined => lookup.beat(id)?.sessionLabel ?? undefined;
  return (
    <li className="flex flex-col gap-1" data-testid="ai-change-group" data-count={n} data-undone={allUndone ? 'true' : undefined}>
      <div className="flex items-start gap-1.5 text-body-s text-fg">
        <span className="w-3.5 flex-none text-ai" aria-hidden="true">{allUndone ? '↺' : ICON[op]}</span>
        <span className={`min-w-0 flex-1${allUndone ? ' line-through text-dim' : ''}`}>
          {before}
          {openable ? (
            <span
              role="button"
              tabIndex={0}
              className="cursor-pointer text-left text-hl hover:underline"
              onClick={(): void => onOpen!(last)}
              onKeyDown={(e): void => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen!(last); } }}
            >
              {name}
            </span>
          ) : (
            <span className={allUndone ? 'text-dim' : 'text-hl'}>{name}</span>
          )}
          {after}
          <span className="text-muted-foreground"> — <span data-testid="ai-change-group-count">{countText}</span></span>{' '}
          <button
            type="button"
            className="text-meta text-dim underline-offset-2 hover:text-fg hover:underline"
            aria-expanded={open}
            onClick={(): void => setOpen(v => !v)}
          >
            {open ? t('aiGroup.hide') : t('aiGroup.show')}
          </button>
        </span>
        {live.length > 0 ? (
          <button
            type="button"
            className="flex-none rounded border border-transparent px-1.5 text-meta text-dim hover:border-line hover:text-fg"
            onClick={(): void => onUndo(turnId, live.map(c => c.id))}
          >
            {related.size > 0 ? fillText(t('aiCard.undoRelated'), { count: String(related.size) }) : t('aiCard.undo')}
          </button>
        ) : null}
      </div>
      {open ? (
        <ul className="ml-5 flex flex-col gap-2 text-ui" data-testid="ai-change-fields">
          {summary.map((ch) => {
            const undone = ch.undone === true;
            const line = changeLine(ch, lookup, t);
            const fieldName = ch.op === 'update' ? changeFieldName(ch, t, locale) : `${line.before}${line.name}${line.after}`;
            return (
              <li key={`${ch.id}:${ch.field ?? ''}`} className={`min-w-0${undone ? ' line-through text-dim' : ' text-fg'}`} data-testid="ai-change-field" data-field={ch.field} data-undone={undone ? 'true' : undefined}>
                <span className={undone ? 'mr-1' : 'mr-1 text-muted-foreground'}>{undone ? '↺ ' : ''}{fieldName}{ch.op === 'update' ? ':' : ''}</span>
                {ch.op === 'update' ? (
                  <FieldDifference change={ch} beatName={ch.field === 'beatOrder' ? beatName : undefined} />
                ) : null}
                {ch.kept === true ? <span className="text-dim">{t('aiCard.kept')}</span> : null}
                {ch.irreversible === true && !undone ? <span className="text-dim">· {t('aiCard.irreversible')}</span> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

function FieldDifference({ change, beatName }: { change: AiChange; beatName?: (id: string) => string | undefined }) {
  const { t } = useI18n();
  const value = (raw: unknown): JSX.Element => {
    const short = changeValue(raw, t, beatName);
    return typeof raw === 'string' && raw.length > 140 ? (
      <details className="group/value min-w-0"><summary className="cursor-pointer break-words"><span className="group-open/value:hidden">{short}</span><span className="hidden group-open/value:inline">{t('aiGroup.hide')}</span></summary><div className="mt-1 whitespace-pre-wrap break-words">{raw}</div></details>
    ) : <span className="whitespace-pre-wrap break-words">{short}</span>;
  };
  return (
    <div className="mt-1 flex min-w-0 flex-col gap-1 text-ui" data-testid="ai-change-diff">
      <div className="rounded bg-danger/10 px-2 py-1" data-testid={change.entity.kind === 'journal' ? 'ai-diff-was' : 'ai-field-was'}><span className="mr-2 text-meta text-muted-foreground">{t('aiCard.was')}</span>{value(change.before)}</div>
      <div className="rounded bg-ok/10 px-2 py-1" data-testid={change.entity.kind === 'journal' ? 'ai-diff-now' : 'ai-field-now'}><span className="mr-2 text-meta text-muted-foreground">{t('aiCard.now')}</span>{value(change.after)}</div>
    </div>
  );
}

/** The card with the names looked up in the live state (the beats of the server state, the journal). */
export function AiTurnCardConnected(props: Omit<AiTurnCardProps, 'lookup'>): JSX.Element {
  const { mp } = useSessionContext();
  const journal = useJournalContext();
  const beats = mp.serverState?.beats;
  const entries = journal.entries;
  const lookup = useMemo<ChangeLookup>(() => ({
    beat: (id) => (beats ?? []).find(b => b.id === id),
    beatOfPrep: (entryId) => (beats ?? []).find(b => b.journalEntryId === entryId),
    entry: (id) => (entries as readonly JournalEntry[]).find(e => e.id === id),
  }), [beats, entries]);
  return <AiTurnCard {...props} lookup={lookup} />;
}

export type { JournalQuestData };
